import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { ExcelStyle, GridApi, GridOptions, SelectionChangedEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../../services";
import { LoadingScreenService } from "../loading-screen.service";
import { TrustedServiceColumnDefinition } from "./ag-grid-column-definition";
import { ModalService } from "src/app/shared/modal.service";
import { TrustedService } from "src/app/generated-models/TrustedService";
import { IsServiceAdmin } from "src/app/utility/userprofile-helper";
import { IUserProfile } from "src/app/npr-request.model";
import { GridViewBaseComponent } from "../../utility/grid-view-base.component";
import { SharedDataService } from "../../services/sharedDataService";
import { exportExcel, getExportedExcelFileNameSuffix } from "src/app/utility/common-helper";
import { AddTrustedServiceModalOptions } from "../add-trusted-service-modal/add-trusted-service.component";
import { NewTrustedService } from "src/app/generated-models/NewTrustedService";

@Component({
  standalone: false,
  templateUrl: "./service-team-trusted-service-management.component.html",
  styleUrls: ["../../styles.scss", "./service-team-trusted-service-management.component.scss"],
})
export class ServiceTeamTrustedServiceManagement extends GridViewBaseComponent implements OnInit {
  gridOptions: GridOptions;
  gridApi: GridApi;
  initPageSize = 15;
  userProfile: IUserProfile;

  // Used in GridViewBaseComponent to store filter onto localStorage
  keyFilterStats = "filter:my-trusted-services";

  rowData: TrustedService[] = [];
  selectedRow: TrustedService;
  selectedRowsCount = 0;
  isAddable = false;
  headerStyle: ExcelStyle[] = [
    {
      id: "header",
      font: {
        bold: true,
      },
    },
  ];

  message: string;

  adminServiceTreeIds: string[];

  constructor(
    protected apiService: ApiService,
    protected modalService: ModalService,
    private loadingService: LoadingScreenService,
    protected sharedDataService: SharedDataService,
    private notificationService: ToastrService,
    private cdr: ChangeDetectorRef
  ) {
    super(apiService, sharedDataService);
  }

  ngOnInit(): void {
    super.InitAgGrid(TrustedServiceColumnDefinition);
    this.gridOptions.rowSelection = "single";
    this.gridOptions.suppressRowClickSelection = false;
    this.apiService.getMyServiceTeamServiceTreeIds().subscribe(
      (response) => {
        this.adminServiceTreeIds = response;
      },
      (err: unknown) => {
        this.adminServiceTreeIds = [];
        this.notificationService.error(err as string);
      }
    );

    this.apiService
      .getUserProfile()
      .then((response) => {
        if (response) {
          this.userProfile = response;
          this.isAddable = IsServiceAdmin(this.userProfile);
        } else {
          this.notificationService.error("user profile returns null, no data was loaded.");
        }
      })
      .catch((error) => {
        this.notificationService.error(`unable to get user profile, error: ${error.message}`);
      });
  }

  onSelectionChanged(event: SelectionChangedEvent): void {
    const nodes = event.api.getSelectedNodes();
    if (nodes && nodes.length > 0) {
      this.selectedRowsCount = nodes.length;
      this.selectedRow = nodes[0].data;
    } else {
      this.selectedRowsCount = 0;
      this.selectedRow = null;
    }
    this.cdr.detectChanges();
  }

  refreshData(): void {
    this.loadingService.setLoading(true);

    this.apiService.getMyTrustedServices().subscribe(
      (response) => {
        this.rowData = response;
        if (this.gridApi) {
          this.gridApi.setGridOption("rowData", this.rowData);
        }
        this.loadingService.setLoading(false);
        setTimeout(() => {
          this.gridApi.autoSizeAllColumns();
        }, 100);
        super.setupFilterByLocalData();
      },
      (e: unknown) => {
        this.rowData = [];
        this.loadingService.setLoading(false);
        this.notificationService.error(e as string);
      }
    );
  }

  async showAddingNewTrustedServiceDialog() {
    const options: AddTrustedServiceModalOptions = {
      save: this.addNewTrustedService.bind(this),
      adminTeamList: this.adminServiceTreeIds,
    };
    await this.modalService.planAddTrustedServiceModal(options);
  }

  isDeletable(): boolean {
    // Only one row could be selected at a time as specified in gridOptions.rowSelection = "single";
    return this.selectedRow && IsServiceAdmin(this.userProfile, this.selectedRow.ServiceTreeId);
  }

  addNewTrustedService(trustedService: NewTrustedService): void {
    this.loadingService.setLoading(true);

    this.apiService.addMyTrustedService(trustedService).subscribe(
      () => {
        this.message = `Trusted service ${trustedService.TrustedServiceTreeId} have been added to ${trustedService.ServiceTreeId} successfully.`;
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
      },
      (error: unknown) => {
        this.notificationService.error(error as string);
        this.loadingService.setLoading(false);
      },
      () => {
        this.refreshData();
      }
    );
  }

  deleteTrustedService(): void {
    if (!this.selectedRow) {
      this.message = "Please select one trusted service to delete";
      this.notificationService.info(this.message);
      return;
    }

    this.apiService.deleteMyTrustedService(this.selectedRow.ServiceTreeId, this.selectedRow.TrustedServiceTreeId).subscribe(
      () => {
        this.message = `Trusted service ${this.selectedRow.TrustedServiceTreeId} has been deleted from service ${this.selectedRow.ServiceName}`;
        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
      },
      (error: unknown) => {
        this.notificationService.error(error as string);
        this.loadingService.setLoading(false);
      },
      () => {
        this.refreshData();
      }
    );
  }

  exportExcel() {
    var fileName = `MyTrustedServices-${getExportedExcelFileNameSuffix()}`;
    var sheetName = "My Trusted Services";

    this.loadingService.setLoading(true);
    exportExcel(this.gridApi, fileName, sheetName);
    this.loadingService.setLoading(false);
  }
}
